/**
 * ChargerCard - Displays and edits a charger configuration
 *
 * Supports charger database selection (auto-populates specs)
 * or manual configuration. Shows charger type, power, quantity, cost, efficiency.
 */

import React from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Autocomplete,
    alpha,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { vortxColors, NumberInput, FormRow } from '@vortx/shared-ui';

/**
 * ChargerCard Component
 *
 * @param {Object} props
 * @param {number} props.index - Position in chargers array
 * @param {Object} props.charger - Charger configuration data
 * @param {Array} props.chargerDatabase - Available chargers from API
 * @param {Function} props.onChange - Callback with updated charger
 * @param {Function} props.onRemove - Callback to remove this charger
 */
function ChargerCard({
    index,
    charger,
    chargerDatabase = [],
    onChange,
    onRemove,
}) {
    const selectedCharger = chargerDatabase.find(c => c.id === charger.charger_database_id) || null;

    const handleFieldChange = (field, value) => {
        onChange({ ...charger, [field]: value });
    };

    const handleChargerSelect = (event, newValue) => {
        if (newValue) {
            onChange({
                ...charger,
                charger_database_id: newValue.id,
                charger_name: newValue.name,
                type: newValue.charger_type,
                power_kw: newValue.power_output_kw,
                cost_per_unit: newValue.equipment_cost,
                efficiency_pct: newValue.efficiency_pct,
                connector_types: newValue.connector_types,
                num_ports: newValue.num_ports,
            });
        }
    };

    const totalPower = (charger.quantity || 1) * (charger.power_kw || 0);
    const totalCost = (charger.quantity || 1) * (charger.cost_per_unit || 0);
    const efficiency = charger.efficiency_pct || (charger.type === 'DCFC' ? 93 : 92);

    return (
        <Box
            sx={{
                p: 2,
                mb: 2,
                bgcolor: alpha(vortxColors.gold, 0.05),
                borderRadius: 1,
                border: `1px solid ${alpha(vortxColors.gold, 0.2)}`,
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Charger Group {index + 1}
                </Typography>
                <IconButton
                    size="small"
                    onClick={onRemove}
                    sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Charger Database Selection */}
            <Autocomplete
                value={selectedCharger}
                onChange={handleChargerSelect}
                options={chargerDatabase}
                groupBy={(option) => option.charger_type === 'DCFC' ? 'DC Fast Charger' : 'Level 2 (AC)'}
                getOptionLabel={(option) => option.name}
                renderOption={(props, option) => (
                    <Box component="li" {...props}>
                        <Box>
                            <Typography variant="body2">{option.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {option.power_output_kw} kW • {option.efficiency_pct}% eff • ${option.equipment_cost?.toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Charger Model"
                        size="small"
                        placeholder="Select from database or configure manually"
                    />
                )}
                sx={{ mb: 2 }}
            />

            {/* Quantity and Power */}
            <FormRow spacing={2}>
                <NumberInput
                    label="Quantity"
                    value={charger.quantity || 1}
                    onChange={(val) => handleFieldChange('quantity', val)}
                    min={1}
                    max={100}
                    decimals={0}
                    sx={{ width: 120 }}
                />
                <NumberInput
                    label="Power per Unit"
                    value={charger.power_kw || 19.2}
                    onChange={(val) => handleFieldChange('power_kw', val)}
                    unit="kW"
                    min={1}
                    max={1000}
                    decimals={1}
                    sx={{ flex: 1, minWidth: 140 }}
                />
                <NumberInput
                    label="Efficiency"
                    value={efficiency}
                    onChange={(val) => handleFieldChange('efficiency_pct', val)}
                    unit="%"
                    min={50}
                    max={100}
                    decimals={1}
                    sx={{ width: 120 }}
                />
            </FormRow>

            {/* Cost */}
            <NumberInput
                label="Cost per Unit"
                value={charger.cost_per_unit || 2500}
                onChange={(val) => handleFieldChange('cost_per_unit', val)}
                unit="$"
                min={0}
                max={500000}
                decimals={0}
                fullWidth
                sx={{ mb: 2 }}
            />

            {/* Summary */}
            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2">
                    <strong>Total:</strong> {totalPower.toLocaleString()} kW capacity • ${totalCost.toLocaleString()} cost • {efficiency}% efficiency
                </Typography>
                {charger.connector_types && charger.connector_types.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                        Connectors: {charger.connector_types.join(', ')}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

export default ChargerCard;
